import { useSyncExternalStore, useCallback, useEffect, useMemo } from 'react';
import { captureException } from '@sentry/browser';
import { tokenManager } from './token-manager.ts';
import { authClient, AuthUserCompanyRole } from './auth-client.ts';
import type { Auth } from './token-manager.ts';

export type { Auth, AuthUserCompanyRole };

export interface UseAuth {
  auth: Auth | null;

  login(username: string, password: string): Promise<void>;

  logout(): Promise<void>;
}

export const useAuthProvider = () => {
  // Single source of truth: TokenManager (localStorage-backed), notifies React on change
  const auth = useSyncExternalStore(tokenManager.subscribe, tokenManager.getSnapshot);

  const login = useCallback(async (username: string, password: string) => {
    const res = await authClient.login(username, password);
    const authUser = await authClient.current(res.accessToken);

    const now = Date.now();
    tokenManager.saveAuth({
      id: authUser.id,
      username: authUser.username,
      email: authUser.email,
      role: authUser.role,
      userCompanyRole: authUser.userCompanyRole,
      accessTokenExpiresAt: new Date(now + res.expiresIn * 1000),
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
    });
  }, []);

  const logout = useCallback(async () => {
    const current = tokenManager.getSnapshot();
    if (current) {
      try {
        await authClient.logout(current.refreshToken);
      } catch (error) {
        captureException(error);
      }
    }
    // Notifies React via useSyncExternalStore -> AuthProtection redirects to login
    tokenManager.clearAuth();
  }, []);

  // On mount: get a valid (possibly refreshed) token first, then refresh user profile
  useEffect(() => {
    async function fetchAuthUser() {
      try {
        const accessToken = await tokenManager.getValidAccessToken();
        if (!accessToken) return;

        const user = await authClient.current(accessToken);
        const current = tokenManager.getSnapshot();
        if (!current) return;

        tokenManager.saveAuth({
          ...current,
          id: user.id,
          username: user.username,
          email: user.email,
          role: user.role,
          userCompanyRole: user.userCompanyRole,
        });
      } catch (error) {
        captureException(error);
      }
    }

    void fetchAuthUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({
      auth,
      login,
      logout,
    }),
    [auth, login, logout],
  );
};
